import { Box, Button, Card, CircularProgress, List, ListItem, Typography } from "@mui/material";
import { useModals } from "../../../../context/ModalContext";
import { invoiceEditorInnerBorder, invoiceEditorSectionCardSx } from "../../../ui/invoiceEditorSectionSurface";
import { InvoiceDetailsView } from "../../view/components/DetailsView";
import { RELATED_DEPOSIT_INVOICE_MODAL_KEY } from "../../RelatedDepositInvoiceModalHost";

interface InvoiceRelatedInvoicesCardProps {
  depositInvoiceUid: string | null | undefined;
  scopeOfWorkUid?: string | null;
  depositAmount?: number | null;
  isLoading?: boolean;
}

export function InvoiceRelatedInvoicesCard({
  depositInvoiceUid,
  scopeOfWorkUid,
  depositAmount,
  isLoading = false,
}: InvoiceRelatedInvoicesCardProps) {
  const { openModal } = useModals();

  if (isLoading) {
    return (
      <Card elevation={0} sx={invoiceEditorSectionCardSx}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={120}>
          <CircularProgress size={28} />
        </Box>
      </Card>
    );
  }

  if (!depositInvoiceUid) {
    return null;
  }

  const formatted =
    depositAmount != null && Number.isFinite(Number(depositAmount))
      ? new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(depositAmount))
      : null;

  const openDeposit = () => {
    openModal(
      { key: RELATED_DEPOSIT_INVOICE_MODAL_KEY, preventParentTrigger: true },
      <InvoiceDetailsView invoiceUid={depositInvoiceUid} scopeOfWorkUid={scopeOfWorkUid ?? undefined} />,
    );
  };

  return (
    <Card elevation={0} sx={invoiceEditorSectionCardSx}>
      <Box sx={{ p: 2, bgcolor: "background.paper", borderBottom: invoiceEditorInnerBorder }}>
        <Typography variant="h6" sx={{ fontWeight: "bold", color: "text.primary" }}>
          Related invoices
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.875rem", lineHeight: 1.6, mt: 0.5 }}>
          The deposit invoice for this quote appears below. When applicable, that payment is credited toward this invoice.
        </Typography>
      </Box>
      <List disablePadding sx={{ bgcolor: "grey.100" }}>
        <ListItem sx={{ py: 1.5, px: 2, bgcolor: "grey.100", alignItems: "flex-start", display: "block" }}>
          {/* Avoid ListItemText secondary (renders as <p>): interactive content inside <p> breaks clicks. */}
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
            Deposit invoice
          </Typography>
          {formatted ? (
            <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.875rem", mt: 0.25 }}>
              Deposit charged: {formatted}
            </Typography>
          ) : null}
          <Button
            type="button"
            variant="text"
            onClick={openDeposit}
            sx={{
              mt: formatted ? 0.5 : 0.25,
              p: 0,
              minWidth: 0,
              justifyContent: "flex-start",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.875rem",
            }}
          >
            View deposit invoice
          </Button>
        </ListItem>
      </List>
    </Card>
  );
}
